import { useCallback } from 'react';

import { Orders } from '@/api/Orders';
import {
  ArtworkPreviewApprovalResponse,
  CommentRequestDataWithFiles,
  CommentResponse,
} from '@/api/data-contracts';

export const useOrderArtworkPreviewMethods = (ordersApi: Orders) => {
  const getArtworkPreviewApprovals = useCallback(
    async (
      orderId: string,
      bookingId: string,
      signal?: AbortSignal | undefined,
    ): Promise<ArtworkPreviewApprovalResponse> => {
      const { data } = await ordersApi.getArtworkPreviewApprovals(orderId, bookingId, {
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const getOrderArtworkPreviewApprovals = useCallback(
    async (
      orderId: string,
      signal?: AbortSignal | undefined,
    ): Promise<ArtworkPreviewApprovalResponse[]> => {
      const { data } = await ordersApi.getOrderArtworkPreviewApprovals(orderId, { signal });
      return data;
    },
    [ordersApi],
  );

  const downloadAttachmentVersion = useCallback(
    async (resourceId: string, attachmentId: string, versionId: number): Promise<Blob> => {
      const response = await ordersApi.downloadAttachmentVersion2(
        resourceId,
        attachmentId,
        versionId,
        {
          format: 'blob',
        } as any,
      );
      return response.data as Blob;
    },
    [ordersApi],
  );

  const approveArtworkPreview = useCallback(
    async (orderId: string, bookingId: string, attachmentId: string) => {
      const { data } = await ordersApi.approveArtworkPreview(orderId, bookingId, attachmentId);
      return data;
    },
    [ordersApi],
  );

  const rejectArtworkPreview = useCallback(
    async (orderId: string, bookingId: string, attachmentId: string, rejectionReason: string) => {
      const { data } = await ordersApi.rejectArtworkPreview(orderId, bookingId, attachmentId, {
        rejectionReason,
      });
      return data;
    },
    [ordersApi],
  );

  const markArtworkPreviewRead = useCallback(
    async (orderId: string, bookingId: string, attachmentId: string): Promise<void> => {
      await ordersApi.markArtworkPreviewRead(orderId, bookingId, attachmentId);
    },
    [ordersApi],
  );

  const getArtworkPreviewComments = useCallback(
    async (
      orderId: string,
      bookingId: string,
      attachmentId: string,
      signal?: AbortSignal | undefined,
    ): Promise<Record<string, CommentResponse[]>> => {
      const { data } = await ordersApi.getArtworkPreviewComments(orderId, bookingId, attachmentId, {
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const addArtworkPreviewComment = useCallback(
    async (
      orderId: string,
      bookingId: string,
      attachmentId: string,
      request: CommentRequestDataWithFiles,
    ) => {
      const { data } = await ordersApi.addArtworkPreviewComment(
        orderId,
        bookingId,
        attachmentId,
        request,
      );
      return data;
    },
    [ordersApi],
  );

  return {
    getArtworkPreviewApprovals,
    getOrderArtworkPreviewApprovals,
    downloadAttachmentVersion,
    approveArtworkPreview,
    rejectArtworkPreview,
    markArtworkPreviewRead,
    getArtworkPreviewComments,
    addArtworkPreviewComment,
  };
};
